import React, { useLayoutEffect, useRef, useState } from "react";
import { metricValue, metricUnit, delta, qualifierKind, sparkBars } from "./reportsFormat.mjs";
import ReportsChart from "./ReportsChart";

/*
 * BAND 2 — the metric grid.
 *
 * A twelve-column grid of widgets. A widget is a number card or a chart, each
 * showing one metric, each as wide as the reader drags it. Gabriel, 2026-10-07:
 * "Allow me to add not just a number card, but also another chart and let me
 * resize it (make it wide or narrow, by dragging)."
 *
 * The layout is the `reports_layout` setting: an ordered list of
 * { id, type: 'card' | 'chart', metric, span (1-12), granularity? }. Before it
 * existed the row was `reports_tiles`, a list of metric ids, and the server
 * turns that into cards when no layout has been saved.
 *
 * WHAT THIS COMPONENT DECIDES: which widgets, in what order, how wide. Nothing
 * else. The value, the comparison, the qualifier sentence and whether an alert
 * is live arrive from src/reports/metrics.js already settled — a tile cannot
 * decide to print a percentage the metric layer suppressed, because it is never
 * given one. The red border is `tile.alert.active` and nothing else.
 */

const COLUMNS = 12;
export const DEFAULT_SPAN = { card: 2, chart: 6 };

/*
 * Spans are saved against a wide screen. On a narrower one a card saved at 2 of
 * 12 would be a sliver, so spans double below 1100px of grid and go full width
 * below 640px (a card keeps half).
 */
function effectiveSpan(item, width) {
  if (width < 640) return item.type === "card" ? 6 : COLUMNS;
  if (width < 1100) return Math.min(COLUMNS, item.span * 2);
  return item.span;
}

/** The drag grip, same six-dot mark as the v1 module it replaces. */
function Grip({ onDragStart, onDragEnd }) {
  return (
    <span
      className="crm-tile__grip"
      title="Drag to reorder"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => e.stopPropagation()}
    >
      <svg viewBox="0 0 6 16" aria-hidden="true">
        {[2.5, 8, 13.5].map((y) => (
          <g key={y}>
            <circle cx="1.4" cy={y} r="1.2" />
            <circle cx="4.6" cy={y} r="1.2" />
          </g>
        ))}
      </svg>
    </span>
  );
}

/*
 * The sparkline. The partial period is hatched rather than solid, because a
 * full-height bar for one day of data is the single most misleading mark the
 * old page drew. A period with no value draws no bar at all.
 */
function Spark({ series }) {
  const bars = sparkBars(series.map((p) => ({ ...p, count: p.count ?? 0, known: p.count != null && p.known !== false })));
  return (
    <div className="crm-tile__spark">
      {bars.map((bar) => (
        <span
          key={bar.key}
          className={
            "crm-tile__bar" + (bar.partial ? " is-partial" : "") + (bar.known === false ? " is-unknown" : "")
          }
          style={{ height: `${bar.height}px` }}
          title={bar.known === false ? `${bar.key}: no value` : `${bar.key}: ${bar.count}${bar.partial ? " (partial)" : ""}`}
        />
      ))}
    </div>
  );
}

/*
 * The right-edge handle. Dragging it moves the widget's span one column at a
 * time; the new width is saved when the pointer lets go, not on every column
 * crossed.
 */
function ResizeHandle({ span, columnWidth, onResize, onCommit }) {
  const start = useRef(null);
  return (
    <span
      className="crm-w__resize"
      title="Drag to resize"
      role="separator"
      aria-orientation="vertical"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // Capture keeps the drag alive when the pointer outruns the handle.
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* not a live pointer */ }
        start.current = { x: e.clientX, span };
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const next = Math.max(1, Math.min(COLUMNS, Math.round(start.current.span + (e.clientX - start.current.x) / columnWidth)));
        onResize(next);
      }}
      onPointerUp={() => {
        if (!start.current) return;
        start.current = null;
        onCommit();
      }}
    />
  );
}

/** The "…" menu both widget types share. */
function WidgetMenu({ open, onToggle, children }) {
  return (
    <>
      <button
        type="button"
        className="crm-tile__menu"
        aria-label="Widget options"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
      >
        …
      </button>
      {open && (
        <div className="crm-pop crm-tile__pop" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </>
  );
}

function CardBody({ tile }) {
  const change = delta(tile.comparison);
  const kind = qualifierKind(tile);
  const hasSeries = Array.isArray(tile.series) && tile.series.length > 0;
  return (
    <>
      <div className="crm-tile__valrow">
        <span className="crm-tile__value crm-num">{metricValue(tile.value, tile.format)}</span>
        {metricUnit(tile.format) && tile.value != null && (
          <span className="crm-tile__unit">{metricUnit(tile.format)}</span>
        )}
        {change && (
          <span
            className={`crm-tile__delta crm-tile__delta--${change.direction} crm-num`}
            title={tile.comparison.basis}
          >
            {change.text}
          </span>
        )}
        {/* A rate has no delta — its context is the counts behind it. */}
        {!change && tile.delta_note && <span className="crm-tile__note crm-num">{tile.delta_note}</span>}
      </div>

      {hasSeries && !tile.empty && <Spark series={tile.series} />}

      {/* Nothing measured in the whole window is its own state, not a zero. */}
      {tile.empty && (
        <div className="crm-tile__none">
          <span className="crm-tile__nonebar" />
          <span>{tile.qualifier || "No events in this window. Widen the date range or remove the tile."}</span>
        </div>
      )}

      {!tile.empty && tile.qualifier && (
        <div className={`crm-tile__qual crm-tile__qual--${kind}`}>{tile.qualifier}</div>
      )}
    </>
  );
}

function Widget({
  item, tile, tiles, library, width, columnWidth, over, expanded, menuOpen,
  onMenu, onChange, onCommit, onRemove, onExpand, onSelectPeriod,
  onDragStart, onDragEnd, onDragOver, onDrop,
}) {
  const span = effectiveSpan(item, width);
  const isChart = item.type === "chart";
  const granularity = item.granularity || tile.default_granularity || "monthly";
  const hasSeries = Array.isArray(tile.series) && tile.series.length > 0;
  // Every card opens in the drill-down now, rates included. Only a tile with no
  // series at all (cost per lead before any spend is recorded) has nothing to draw.
  const expandable = !isChart && hasSeries && !(tile.empty && tile.value == null);

  return (
    <div
      className={
        `crm-tile crm-w crm-w--${item.type} crm-tile--${tile.variant}` +
        (tile.alert && tile.alert.active ? " crm-tile--alert" : "") +
        (over ? " crm-tile--over" : "") +
        (tile.empty ? " crm-tile--empty" : "") +
        (expandable ? " crm-tile--expandable" : "") +
        (expanded ? " crm-tile--expanded" : "")
      }
      style={{ gridColumn: `span ${span}` }}
      onClick={expandable ? () => onExpand(tile.id) : undefined}
      onDragOver={(e) => { e.preventDefault(); onDragOver(); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
    >
      <div className="crm-tile__head">
        <Grip onDragStart={onDragStart} onDragEnd={onDragEnd} />
        {isChart ? (
          <select
            className="crm-w__metric"
            value={item.metric}
            aria-label="Metric"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { onChange({ ...item, metric: e.target.value, granularity: null }); onCommit(); }}
          >
            {library.map((entry) => (
              <option key={entry.id} value={entry.id}>{(tiles[entry.id] && tiles[entry.id].label) || entry.id}</option>
            ))}
          </select>
        ) : (
          <span className="crm-tile__label">{tile.label}</span>
        )}
        {isChart && (
          <span className="crm-seg crm-seg--scope crm-w__grain" role="group" aria-label="Chart granularity">
            {["weekly", "monthly"].map((g) => (
              <button
                key={g}
                type="button"
                className={granularity === g ? "sel" : ""}
                onClick={(e) => { e.stopPropagation(); onChange({ ...item, granularity: g }); onCommit(); }}
              >
                {g === "weekly" ? "W" : "M"}
              </button>
            ))}
          </span>
        )}
        <WidgetMenu open={menuOpen} onToggle={onMenu}>
          <div className="crm-tile__popnote">
            {tile.comparison ? tile.comparison.basis : tile.qualifier || "No comparison for this metric."}
          </div>
          <div className="crm-pop__divider">
            {!isChart && expandable && (
              <button className="crm-linkact" onClick={() => { onMenu(); onExpand(tile.id); }}>
                {expanded ? "Collapse chart" : "Open in the chart"}
              </button>
            )}
            <button
              className="crm-linkact"
              onClick={() => {
                onMenu();
                const type = isChart ? "card" : "chart";
                onChange({ ...item, type, span: DEFAULT_SPAN[type], granularity: null });
                onCommit();
              }}
            >
              {isChart ? "Turn into a number card" : "Turn into a chart"}
            </button>
            <button className="crm-linkact" onClick={onRemove}>Remove</button>
          </div>
        </WidgetMenu>
      </div>

      {isChart ? (
        <>
          <div className="crm-w__chartval">
            <span className="crm-num">{metricValue(tile.value, tile.format)}</span>
            {metricUnit(tile.format) && tile.value != null && <span className="crm-tile__unit">{metricUnit(tile.format)}</span>}
            <span className="crm-w__chartnote">{tile.qualifier}</span>
          </div>
          {hasSeries ? (
            <ReportsChart
              tile={tile}
              granularity={granularity}
              height={200}
              onSelectPeriod={(period) => onSelectPeriod(tile, period)}
            />
          ) : (
            <div className="crm-tile__none"><span className="crm-tile__nonebar" /></div>
          )}
        </>
      ) : (
        <CardBody tile={tile} />
      )}

      <ResizeHandle
        span={item.span}
        columnWidth={columnWidth}
        onResize={(next) => onChange({ ...item, span: next })}
        onCommit={onCommit}
      />
    </div>
  );
}

/*
 * "+ add" — a dashed slot the shape of a card, at the end of the grid, so it
 * shows where the next widget lands. Picks the kind first, then the metric.
 */
function AddSlot({ library, tiles, layout, onAdd }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("card");
  // A metric can be a card once; charts can repeat (the same metric weekly and monthly, say).
  const cards = new Set(layout.filter((w) => w.type === "card").map((w) => w.metric));
  const available = library.filter((entry) => type === "chart" || !cards.has(entry.id));
  const groups = available.reduce((acc, entry) => {
    (acc[entry.group] = acc[entry.group] || []).push(entry);
    return acc;
  }, {});

  return (
    <div className="crm-tile crm-tile--add" style={{ gridColumn: "span 2" }}>
      <button type="button" className="crm-tile__addbtn" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
        + add
      </button>
      {open && (
        <div className="crm-pop crm-tile__addpop" onClick={(e) => e.stopPropagation()}>
          <span className="crm-seg crm-seg--scope crm-tile__addkind" role="group" aria-label="Widget kind">
            {[["card", "Number"], ["chart", "Chart"]].map(([value, label]) => (
              <button key={value} type="button" className={type === value ? "sel" : ""} onClick={() => setType(value)}>
                {label}
              </button>
            ))}
          </span>
          {Object.entries(groups).map(([group, entries]) => (
            <div key={group}>
              <h4>{group}</h4>
              {entries.map((entry) => (
                <button key={entry.id} onClick={() => { setOpen(false); onAdd(type, entry.id); }}>
                  <b>{(tiles[entry.id] && tiles[entry.id].label) || entry.id}</b>
                  <span className="crm-num">{entry.about}</span>
                </button>
              ))}
            </div>
          ))}
          {!available.length && <div className="crm-hint">Every metric is already on the page as a card.</div>}
        </div>
      )}
    </div>
  );
}

export default function ReportsTiles({
  tiles, library, layout, expandedId, filters, filteredBy,
  onLayout, onExpand, onSelectPeriod,
}) {
  const [menuFor, setMenuFor] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  // Edits in flight (a resize mid-drag) live here and are saved on commit.
  const [draft, setDraft] = useState(null);
  const items = draft || layout;

  const gridRef = useRef(null);
  const [width, setWidth] = useState(1400);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const GAP = 14;
  const columnWidth = (width + GAP) / COLUMNS;

  // A saved layout can name a metric the library no longer builds; skip it
  // rather than rendering a hole.
  const rendered = items.filter((w) => tiles[w.metric]);

  // Held in a ref as well, so a change and its commit in one handler see each other.
  const draftRef = useRef(null);
  const change = (next) => {
    draftRef.current = (draftRef.current || layout).map((w) => (w.id === next.id ? next : w));
    setDraft(draftRef.current);
  };
  const commit = () => {
    const current = draftRef.current;
    draftRef.current = null;
    setDraft(null);
    if (current) onLayout(current);
  };

  function drop(targetId) {
    const from = items.findIndex((w) => w.id === dragId);
    const to = items.findIndex((w) => w.id === targetId);
    setDragId(null);
    setOverId(null);
    if (from < 0 || to < 0 || from === to) return;
    const next = items.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    onLayout(next);
  }

  return (
    <section className="crm-tiles-band" onClick={() => setMenuFor(null)}>
      <div className="crm-funnel__head">
        <span className="crm-funnel__eyebrow">Metrics</span>
        <span className="crm-funnel__caption">
          same dates as the funnel · click any number to chart it · drag the grip to
          reorder, the right edge to resize
          {/* When a filter is on but did not reach these numbers, the row has to
              say so. A page that claims to be filtered and shows unfiltered
              tiles is worse than one that shows no filter at all. */}
          {filters && filters.stage && !(filteredBy || []).includes("stage") && (
            <>
              {" · "}
              <b>counts not narrowed by the stage filter</b> — a stage is a cohort predicate and the
              counts are events
            </>
          )}
        </span>
      </div>

      <div className="crm-tiles crm-wgrid" ref={gridRef}>
        {rendered.map((item) => (
          <Widget
            key={item.id}
            item={item}
            tile={tiles[item.metric]}
            tiles={tiles}
            library={library}
            width={width}
            columnWidth={columnWidth}
            over={overId === item.id && dragId !== item.id}
            expanded={item.type === "card" && expandedId === item.metric}
            menuOpen={menuFor === item.id}
            onMenu={() => setMenuFor(menuFor === item.id ? null : item.id)}
            onChange={change}
            onCommit={commit}
            onRemove={() => { setMenuFor(null); onLayout(items.filter((w) => w.id !== item.id)); }}
            onExpand={onExpand}
            onSelectPeriod={onSelectPeriod}
            onDragStart={() => setDragId(item.id)}
            onDragEnd={() => { setDragId(null); setOverId(null); }}
            onDragOver={() => setOverId(item.id)}
            onDrop={() => drop(item.id)}
          />
        ))}
        <AddSlot
          library={library}
          tiles={tiles}
          layout={items}
          onAdd={(type, metric) =>
            onLayout([
              ...items,
              { id: `${type}-${metric}-${Date.now().toString(36)}`, type, metric, span: DEFAULT_SPAN[type] },
            ])
          }
        />
      </div>
    </section>
  );
}
